import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { promoRedeemInput } from '@xdocs/schemas';
import type { ErrorAlert, PromoAppliesTo, PromoRedeemEffect } from '@xdocs/schemas';
import { useAuth } from '../../../auth/AuthProvider.js';
import { useWs } from '../../../ws/WsProvider.js';

export interface UsePromoRedeemResult {
  code: string;
  setCode: (code: string) => void;
  status: 'idle' | 'validating' | 'applied' | 'invalid';
  effect: PromoRedeemEffect | undefined;
  errorMessage: string | undefined;
  apply: () => Promise<void>;
  remove: () => void;
}

/**
 * F56 promo-code field, shared by the F24 upgrade flow and the F57
 * buy-credits flow. `promo.redeem` is the REAL redemption (not a dry
 * preview) — server enforces `max_redemptions`/`per_user_limit` in one txn
 * (§15 hard rule) and, for a `bonus_credits` code, grants immediately via
 * `credit_ledger`. For `percent_off`/`fixed_off`, the resolved discount is
 * re-validated again when `billing.checkout.start` is called with the SAME
 * `code` (race-safety, `billing.dto.ts`'s own doc comment) — this hook's
 * `code` is what the caller must forward as `checkoutStartInput.promoCode`.
 */
export function usePromoRedeem(appliesTo: PromoAppliesTo, appliesToRefId?: string): UsePromoRedeemResult {
  const { wsClient } = useWs();
  const queryClient = useQueryClient();
  const { userId } = useAuth();
  const [code, setCodeState] = useState('');
  const [status, setStatus] = useState<'idle' | 'validating' | 'applied' | 'invalid'>('idle');
  const [effect, setEffect] = useState<PromoRedeemEffect | undefined>(undefined);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const mutation = useMutation<PromoRedeemEffect, ErrorAlert, void>({
    mutationFn: async () => {
      const payload = promoRedeemInput.parse({ code: code.trim(), appliesTo, ...(appliesToRefId ? { appliesToRefId } : {}) });
      const result = await requestTyped(wsClient, 'promo.redeem', payload);
      return result.effect;
    },
    onSuccess: (resolvedEffect) => {
      setEffect(resolvedEffect);
      setStatus('applied');
      if (resolvedEffect.kind === 'bonus_credits') {
        void queryClient.invalidateQueries({ queryKey: ['user', userId, 'credits'] });
      }
    },
    onError: (err) => {
      setStatus('invalid');
      setErrorMessage(err.message);
    },
  });

  function setCode(next: string): void {
    setCodeState(next);
    if (status !== 'idle') {
      setStatus('idle');
      setEffect(undefined);
      setErrorMessage(undefined);
    }
  }

  async function apply(): Promise<void> {
    if (!code.trim()) {
      setStatus('invalid');
      setErrorMessage('settingsBilling:promo.error.required');
      return;
    }
    setStatus('validating');
    setErrorMessage(undefined);
    try {
      await mutation.mutateAsync();
    } catch {
      // state already set by onError
    }
  }

  function remove(): void {
    setCodeState('');
    setStatus('idle');
    setEffect(undefined);
    setErrorMessage(undefined);
  }

  return { code, setCode, status, effect, errorMessage, apply, remove };
}
